/**
 * SourceStampTag — net-new shared component (holistic plan §Net-new, S4.3).
 *
 * The referenced-lens provenance stamp: a REFERENCED row is visibly not
 * owned — every row carries its source system (來源:會計 / 來源:船務), the
 * projection's syncedAt, and an optional drill-through to the owning module
 * (write-back always happens THERE; the referencing surface never edits).
 *
 * Namespace-agnostic by design: all user-facing strings arrive via props so
 * each consumer localizes from its own i18n namespace.
 */

import { Button, Tag, Tooltip, theme } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

export interface SourceStampTagProps {
  /** Localized source label, e.g. 來源：會計 / Source: Finance. */
  sourceLabel: string;
  /** ISO stamp of the read projection. */
  syncedAt: string;
  /** Localized drill affordance label (tooltip + aria); omit to hide the drill. */
  drillLabel?: string;
  onDrill?: () => void;
}

const SourceStampTag = ({ sourceLabel, syncedAt, drillLabel, onDrill }: SourceStampTagProps) => {
  const { token } = theme.useToken();
  const stamp = dayjs(syncedAt).format('MM-DD HH:mm');

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}>
      <Tag
        style={{
          margin: 0,
          backgroundColor: token.colorFillQuaternary,
          color: token.colorTextSecondary,
          borderColor: token.colorBorderSecondary,
          borderRadius: token.borderRadiusSM,
        }}
      >
        {sourceLabel} · <span style={{ fontVariantNumeric: 'tabular-nums' }}>{stamp}</span>
      </Tag>
      {onDrill && drillLabel && (
        <Tooltip title={drillLabel}>
          <Button
            type="text"
            size="small"
            aria-label={drillLabel}
            icon={<ExportOutlined style={{ color: token.colorPrimary }} />}
            onClick={(event) => {
              event.stopPropagation();
              onDrill();
            }}
          />
        </Tooltip>
      )}
    </span>
  );
};

export default SourceStampTag;
